import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { useAuthModal } from "../context/AuthModalContext";
import { useTranslation } from "react-i18next";

export default function LoginModal() {
  const { isOpen, closeModal } = useAuthModal();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, closeModal]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleGuest = () => {
    localStorage.setItem("isGuest", "true");
    localStorage.removeItem("guestBannerDismissed");
    window.dispatchEvent(new Event("guestStateChanged"));
    closeModal();
    navigate("/");
  };

  const btnBase: React.CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: "10px", padding: "13px 20px", borderRadius: "10px",
    fontSize: "14px", fontWeight: 600, cursor: "pointer",
    transition: "border-color 0.2s, box-shadow 0.2s",
    width: "100%",
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            width: "100vw", height: "100vh",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            overflowY: "auto",
            padding: "16px",
            boxSizing: "border-box",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 24 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            onClick={e => e.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "420px",
              margin: "0 auto",
              background: "#F5F2EE",
              borderRadius: "20px",
              padding: "36px 32px",
              zIndex: 10000,
              border: "1px solid rgba(201,168,76,0.3)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.4)",
            }}
          >
            {/* Close */}
            <button
              onClick={closeModal}
              style={{ position: "absolute", top: "14px", right: "14px", background: "transparent", border: "none", cursor: "pointer", color: "#8C8C8C", fontSize: "18px", lineHeight: 1, padding: "6px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "6px" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#EAE6E0"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              ✕
            </button>

            {/* Logo */}
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
                <img
                  src="/taxthinkerlogo.png"
                  alt="TaxThinker"
                  style={{ height: "48px", width: "auto", objectFit: "contain" }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const target = e.currentTarget;
                    target.style.display = "none";
                    const next = target.nextElementSibling as HTMLElement;
                    if (next) next.style.display = "flex";
                  }}
                />
                <div style={{ display: "none", alignItems: "center", gap: "10px" }}>
                  <div style={{ background: "#C9A84C", width: "40px", height: "40px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "20px", color: "#2C2C2C", fontStyle: "italic" }}>T</div>
                  <span style={{ color: "#2C2C2C", fontWeight: 800, fontSize: "22px" }}>Tax<span style={{ color: "#C9A84C" }}>Thinker</span></span>
                </div>
              </div>
              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#2C2C2C", marginBottom: "4px" }}>Welcome to TaxThinker</h2>
              <p style={{ fontSize: "13px", color: "#8C8C8C" }}>Choose how you'd like to continue</p>
            </div>

            {/* Auth options */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
              {/* Google */}
              <a
                href="/api/auth/google"
                style={{ ...btnBase, background: "white", border: "1.5px solid #D8D4CE", color: "#2C2C2C", textDecoration: "none" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#C9A84C"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(201,168,76,0.2)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#D8D4CE"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t("login.google")}
              </a>

              {/* Phone - Coming Soon */}
              <button
                disabled
                style={{ ...btnBase, background: "white", border: "1.5px solid #D8D4CE", color: "#ABABAB", cursor: "not-allowed", opacity: 0.55 }}
              >
                <span style={{ fontSize: "18px" }}>📱</span>
                {t("login.phone")}
                <span style={{ fontSize: "11px", background: "#EAE6E0", color: "#8C8C8C", padding: "2px 7px", borderRadius: "4px", fontWeight: 600, flexShrink: 0 }}>{t("login.comingSoon")}</span>
              </button>

              {/* Divider */}
              <div style={{ position: "relative", textAlign: "center", margin: "2px 0" }}>
                <div style={{ height: "1px", background: "#D8D4CE", position: "absolute", top: "50%", width: "100%" }} />
                <span style={{ background: "#F5F2EE", padding: "0 12px", fontSize: "12px", color: "#8C8C8C", position: "relative" }}>or</span>
              </div>

              {/* Guest */}
              <button
                onClick={handleGuest}
                style={{ display: "block", textAlign: "center", padding: "10px", fontSize: "13px", color: "#A07828", fontWeight: 600, background: "none", border: "none", cursor: "pointer", width: "100%" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#C9A84C"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#A07828"; }}
              >
                Continue as Guest →
              </button>
            </div>

            <p style={{ fontSize: "11px", color: "#8C8C8C", textAlign: "center", lineHeight: "1.6", marginTop: "8px" }}>
              By continuing, you agree to our{" "}
              <a href="/terms" style={{ color: "#C9A84C", textDecoration: "none" }}>Terms</a>{" "}
              and{" "}
              <a href="/privacy" style={{ color: "#C9A84C", textDecoration: "none" }}>Privacy Policy</a>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
